<template>
  <el-dialog 
    v-if="visible" 
    title="控制器波形" 
    :visible.sync="visible" 
    width="90%"
    top="5vh"
    :before-close="handleClose" 
    :close-on-click-modal="true">
    <div class="fault_box">
      <!-- 设备信息区域 -->
      <div v-if="rowData" class="device-info-section">
        <!-- 设备信息 -->
        <div class="info-row">
          <div class="info-item">
            <span class="info-label">网关地址：</span>
            <span class="info-value">{{ rowData.macAddr || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">触发时间：</span>
            <span class="info-value">{{ rowData.triggerTime || '-' }}</span>
          </div>
          <!-- 导航按钮区域 -->
          <div class="navigation-item">
            <el-button 
              size="mini" 
              :disabled="!hasPrevious" 
              @click="handlePrevious"
              icon="el-icon-arrow-left"
              class="nav-btn">
              上一条
            </el-button>
            <span class="navigation-info">
              {{ currentIndex + 1 }} / {{ tableData.length }}
            </span>
            <el-button 
              size="mini" 
              :disabled="!hasNext" 
              @click="handleNext"
              icon="el-icon-arrow-right"
              class="nav-btn">
              下一条
            </el-button>
          </div>
        </div>
      </div>
      
      <!-- 波形图区域 -->
      <wave-chart v-if="waveData" :id="recordId" :wave-data="waveData"></wave-chart>
      <div v-else-if="loading" class="loading-tip">
        <i class="el-icon-loading"></i>
        <span>正在加载波形数据...</span>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import waveChart from "./waveChart.vue";
import { waveView } from "@/api/collect/wave";

export default {
  name: 'WaveDialog',
  components: {
    waveChart
  },
  props: {
    // 弹窗显示状态
    visible: {
      type: Boolean,
      default: false
    },
    // 记录ID
    recordId: {
      type: [String, Number],
      default: ''
    },
    // 行数据
    rowData: {
      type: Object,
      default: null
    },
    // 表格数据
    tableData: {
      type: Array,
      default: () => []
    },
    // 当前行索引
    currentIndex: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {
      waveData: null, // 波形数据
      loading: false, // 加载状态
      loadTimer: null // 加载定时器，用于防抖
    };
  },
  watch: {
    // 监听visible变化，当弹窗显示时加载数据
    visible(newVal) {
      if (newVal && this.recordId) {
        // 清除之前的定时器
        if (this.loadTimer) {
          clearTimeout(this.loadTimer);
        }
        // 使用防抖机制，避免重复调用
        this.loadTimer = setTimeout(() => {
          // 使用 $nextTick 确保弹窗DOM渲染完成后再加载数据
          this.$nextTick(() => {
            this.loadWaveData();
          });
        }, 300);
      } else if (!newVal) {
        // 弹窗关闭时重置数据
        this.waveData = null;
      }
    },
    
    // 监听recordId变化，当切换记录时重新加载波形数据
    recordId(newVal, oldVal) {
      if (newVal && newVal !== oldVal && this.visible) {
        // 清除之前的定时器
        if (this.loadTimer) {
          clearTimeout(this.loadTimer);
        }
        // 使用防抖机制，避免重复调用
        this.loadTimer = setTimeout(() => {
          this.loadWaveData();
        }, 100);
      }
    }
  },
  beforeDestroy() {
    // 清理定时器
    if (this.loadTimer) {
      clearTimeout(this.loadTimer);
    }
  },
  computed: {
    // 是否有上一条记录
    hasPrevious() {
      return this.currentIndex > 0;
    },
    
    // 是否有下一条记录
    hasNext() {
      return this.currentIndex >= 0 && this.currentIndex < this.tableData.length - 1;
    }
  },
  
  methods: {
    // 加载波形数据
    async loadWaveData() {
      if (!this.recordId) return;
      
      // 重置数据状态
      this.waveData = null;
      this.loading = true;
      
      try {
        const res = await waveView(this.recordId);
        if (res.code === 200 && res.data) {
          this.waveData = res.data;
        } else {
          // 获取数据失败，关闭弹框并显示错误消息
          this.$emit('update:visible', false);
        }
      } catch (error) {
        console.error('获取波形数据失败:', error);
        // 发生错误时关闭弹框
        this.$emit('update:visible', false);
      } finally {
        this.loading = false;
      }
    },
    
    // 切换到上一条记录
    handlePrevious() {
      if (this.hasPrevious) {
        const previousIndex = this.currentIndex - 1;
        const previousRow = this.tableData[previousIndex];
        this.$emit('switch-record', previousRow, previousIndex);
        this.$emit('update:record-id', previousRow.id); // 更新recordId
      }
    },
    
    // 切换到下一条记录
    handleNext() {
      if (this.hasNext) {
        const nextIndex = this.currentIndex + 1;
        const nextRow = this.tableData[nextIndex];
        this.$emit('switch-record', nextRow, nextIndex);
        this.$emit('update:record-id', nextRow.id); // 更新recordId
      }
    },
    
    // 关闭弹窗
    handleClose() {
      this.$emit('update:visible', false);
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.fault_box {
  min-height: 350px;
  width: 100%;
  position: relative;
}

/* 设备信息区域样式 */
.device-info-section {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 16px 20px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* 设备信息样式 */
.info-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px 40px;
}

.info-item {
  display: flex;
  align-items: center;
  min-width: 200px;
}

.info-label {
  color: #606266;
  font-size: 14px;
  font-weight: 500;
  margin-right: 8px;
  white-space: nowrap;
}

.info-value {
  color: #303133;
  font-size: 14px;
  font-weight: 600;
  background: #ffffff;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  min-width: 120px;
  text-align: center;
}

/* 导航按钮区域样式 */
.navigation-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.nav-btn {
  min-width: 80px;
}

.navigation-info {
  color: #606266;
  font-size: 14px;
  font-weight: 500;
  background: #ffffff;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  min-width: 60px;
  text-align: center;
}

.loading-tip,
.error-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #909399;
  font-size: 14px;
}

.loading-tip i,
.error-tip i {
  font-size: 24px;
  margin-right: 8px;
}

.loading-tip i {
  color: #00d8c3;
  animation: rotating 2s linear infinite;
}


@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
.el-dialog__body {
  padding: 5px 15px 15px 15px !important;
}
</style>
